import {
  createContext,
  useEffect,
  useContext,
  useCallback,
  useReducer,
  useMemo,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthContext } from '../auth/useAuthContext';

// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
import { notify } from 'src/components/notify';

// ----------------------------------------------------------------------
const initialState = {
  vendors: [],
  vendor_list: [],
  page: 0,
  totalPages: 1,
  count: 0,
  totalRecords: null,
  recordPerPage: 15,
  error: false,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      ...state,
      vendor_list: action.payload.vendors,
      page: action.payload.page,
      totalPages: action.payload.totalPages,
      count: action.payload._count,
      totalRecords: action.payload.totalRecords,
      recordPerPage: action.payload.recordPerPage,
    };
  }
  if (action.type === 'DROPDOWN') {
    return {
      ...state,
      vendors: action.payload.vendors,
    };
  }
  if (action.type === 'ERROR') {
    return {
      ...state,
      error: true,
    };
  }
  if (action.type === 'PAGE_CHANGE') {
    return {
      ...state,
      page: action.payload.page,
    };
  }
  if (action.type === 'PAGE_SIZE_CHANGE') {
    return {
      ...state,
      recordPerPage: action.payload.recordPerPage,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const VendorContext = createContext(initialState);

export const useVendorContext = () => {
  const context = useContext(VendorContext);

  if (!context) throw new Error('useSettingsContext must be use inside SettingsProvider');

  return context;
};

export function VendorProvider() {
  const { user, logout } = useAuthContext();
  const storageAvailable = localStorageAvailable();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getVendor = useCallback(
    async (page = 0, isFilter = true) => {
      const pagination = false;
      try {
        const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
        const config = {
          method: 'get',
          maxBodyLength: Infinity,
          url: '/system/partners',
          headers: {
            authorization: `Bearer ${accessToken}`,
            user,
            'Content-Type': 'application/json',
          },
          params: { type: 'Vendor', page, pagination },
        };
        const response = await axios.request(config);
        const { payload } = response.data;
        let filterPayload = payload;
        if (isFilter) {
          filterPayload = payload.filter((it) => it?.mode === localStorage.getItem('mode'));
        }
        dispatch({
          type: 'DROPDOWN',
          payload: {
            vendors: filterPayload,
          },
        });
      } catch (error) {
        dispatch({
          type: 'ERROR',
        });
        if (error === 'Unauthorized request') {
          logout();
        }
      }
    },
    [storageAvailable, user, logout]
  );
  const getVendorList = useCallback(
    async (page = 0, search, pageSize = state.recordPerPage, self_vendor = false) => {
      try {
        const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
        const config = {
          method: 'get',
          maxBodyLength: Infinity,
          url: '/system/partners',
          headers: {
            authorization: `Bearer ${accessToken}`,
            user,
            'Content-Type': 'application/json',
          },
          params: { type: 'Vendor', page, page_size: pageSize, search: search, self_vendor },
        };
        const response = await axios.request(config);

        const { payload, currentPage, totalPages, recordPerPage, totalRecords } = response.data;
        dispatch({
          type: 'INITIAL',
          payload: {
            vendors: payload,
            page: currentPage,
            totalPages: Math.ceil(totalRecords / pageSize),
            recordPerPage: pageSize,
            totalRecords,
          },
        });
      } catch (error) {
        dispatch({
          type: 'ERROR',
        });
        if (error === 'Unauthorized request') {
          logout();
        }
      }
    },
    [storageAvailable, user, logout, state.recordPerPage]
  );

  useEffect(() => {
    getVendorList();
  }, [getVendorList]);

  // useEffect(() => {
  //   getVendor();
  // }, [getVendor]);

  const pageChange = useCallback(
    (page) => {
      getVendorList(page, '', state.recordPerPage);
    },
    [getVendorList, '', state.recordPerPage]
  );
  const setPage = useCallback(
    (updater) => {
      const newPage = typeof updater === 'function' ? updater(state.page) : updater;

      dispatch({ type: 'PAGE_CHANGE', payload: { page: newPage } });
      getVendorList(newPage, '', state.recordPerPage);
    },
    [getVendorList, state.recordPerPage, '', state.page]
  );

  const setRecordPerPage = useCallback(
    (recordPerPage) => {
      dispatch({ type: 'PAGE_SIZE_CHANGE', payload: { recordPerPage } });
      getVendorList(0, '', recordPerPage);
    },
    [getVendorList]
  );
  const pageRefreshChange = useCallback((page, search, page_size, self_vendor) => {
    getVendorList(page, search, page_size, self_vendor);
  },
    [getVendorList]
  );

  const createVendor = useCallback(
    async (data) => {
      try {
        const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: '/system/partners/onboard',
          headers: {
            authorization: `Bearer ${accessToken}`,
            user,
            'Content-Type': 'application/json',
          },
          data,
        };
        const response = await axios.request(config);
        getVendorList();
        return response;
      } catch (err) {
        console.log(err);
        notify(err);
      } finally {
      }
    },
    [getVendorList, storageAvailable, user]
  );

  const getVendorBasicInfo = useCallback(
    async (id) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `/system/partners/${id}/information`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
      };
      const response = await axios.request(config);
      getVendorList();
      return response.data;
    },
    [getVendorList, storageAvailable, user]
  );

  const updateVendorInfo = useCallback(
    async (id, data, status) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'put',
        maxBodyLength: Infinity,
        url: `/system/partners/${id}/information`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data: { ...data, status },
      };

      await axios.request(config);

      getVendorList();
    },
    [getVendorList, storageAvailable, user]
  );

  const updateVendorDocs = useCallback(
    async (id, data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'put',
        maxBodyLength: Infinity,
        url: `/system/partners/${id}/document`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
      };
      await axios.request(config);
      getVendorList();
    },
    [getVendorList, storageAvailable, user]
  );

  const getVendorBankInfo = useCallback(
    async (id) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `/system/partners/${id}/bankacc`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
      };
      const response = await axios.request(config);
      getVendorList();
      return response.data;
    },
    [getVendorList, storageAvailable, user]
  );

  const updateVendorBankInfo = useCallback(
    async (id, data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'put',
        maxBodyLength: Infinity,
        url: `/system/partners/${id}/bank`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
      };
      await axios.request(config);
      getVendorList();
    },
    [getVendorList, storageAvailable, user]
  );

  const updateConfig = useCallback(
    async (id, data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `/service/accounts_service/v1/accounts/vendor_config/${id}`,

        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data: { config_list: data },
      };
      const response = await axios.request(config);
      return response;
    },
    [storageAvailable, user]
  );
  const updateFeature = useCallback(
    async (id, data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'put',
        maxBodyLength: Infinity,
        url: `/system/partners/${id}/features`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
      };
      const response = await axios.request(config);
      return response;
    },
    [storageAvailable, user]
  );

  const getAllServices = useCallback(
    async (id) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `/system/services`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
      };
      const response = await axios.request(config);
      return response.data;
    },
    [storageAvailable, user]
  );

  const memoizedValue = useMemo(
    () => ({
      vendors: state.vendors,
      vendor_list: state.vendor_list,
      page: state.page,
      totalPages: state.totalPages,
      totalRecords: state.totalRecords,
      recordPerPage: state.recordPerPage,
      createVendor,
      getVendorBasicInfo,
      updateVendorDocs,
      updateVendorInfo,
      getVendorBankInfo,
      updateVendorBankInfo,
      getAllServices,
      pageChange,
      getVendorList,
      updateConfig,
      updateFeature,
      pageRefreshChange,
      getVendor,
      setRecordPerPage,
      setPage,
    }),
    [
      state.vendors,
      state.page,
      state.vendor_list,
      state.totalPages,
      state.totalRecords,
      state.recordPerPage,
      createVendor,
      getVendorBasicInfo,
      updateVendorDocs,
      updateVendorInfo,
      getVendorBankInfo,
      updateVendorBankInfo,
      getAllServices,
      pageChange,
      getVendorList,
      updateConfig,
      updateFeature,
      pageRefreshChange,
      getVendor,
      setRecordPerPage,
      setPage,
    ]
  );

  return (
    <VendorContext.Provider value={memoizedValue}>
      <Outlet />
    </VendorContext.Provider>
  );
}
